"use client";

import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import Link from "next/link";
import { motion } from "framer-motion";
import Image from "next/image";
import { useState, useEffect } from "react";
import { MdChevronRight } from "react-icons/md";

const COUNTRIES_LIST = "/static/maps/countries.geo.json";
const WORLD_TOPO_JSON = COUNTRIES_LIST;

const countryCenters: Record<
  string,
  { coords: [number, number]; flag: string }
> = {
  India: { coords: [78.9629, 20.5937], flag: "/static/flags/in.svg" },
  Ghana: { coords: [-1.0232, 7.9465], flag: "/static/flags/gh.svg" },
  Kenya: { coords: [37.9062, -0.0236], flag: "/static/flags/ke.svg" },
  "Southeast Asia": {
    coords: [101.9758, 4.2105],
    flag: "/static/images/asean.png",
  },
};

export default function WorldMap() {
  const [selectedCountry, setSelectedCountry] = useState<string>("India");
  const [flagImages, setFlagImages] = useState<Record<string, string>>({});

  useEffect(() => {
    // Preload flag images as data URLs for cross-browser compatibility
    const loadFlags = async () => {
      for (const [name, { flag }] of Object.entries(countryCenters)) {
        try {
          const response = await fetch(flag);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            setFlagImages((prev) => ({
              ...prev,
              [name]: reader.result as string,
            }));
          };
          reader.readAsDataURL(blob);
        } catch (error) {
          console.error(`Failed to load flag for ${name}:`, error);
        }
      }
    };

    loadFlags();
  }, []);

  const getCountryLink = (country: string) => {
    switch (country.toLowerCase()) {
      case "india":
        return "/country-engagement/india";
      case "kenya":
        return "/country-engagement/kenya";
      case "southeast asia":
        return "/country-engagement/southeast-asia";
      default:
        return "#";
    }
  };

  const isLinkAvailable = (country: string) => {
    return ["india", "kenya", "southeast asia"].includes(country.toLowerCase());
  };

  return (
    <div className="relative py-[120px] desktop:!pb-0 betweenMobileTab:py-16 mobileMax:py-10 bg-[#003350]">
      <div className="mini-container">
        <h2 className="desktop:text-[52px] font-poppins font-semibold leading-normal text-white text-center text-[48px] mobileMax:text-[28px] mobileMax:px-0 mb-[80px] betweenMobileTab:mb-0 mobileMax:mb-0">
          Country Engagement
        </h2>
        <div className="flex mobileToDesk:flex-col mobileToDesk:items-center desktop:pb-20">
          <div className="flex flex-col items-center justify-center pt-4 mobileMax:pt-0 betweenMobileTab:mt-0 relative w-full">
            <div className="w-full max-w-[900px] relative">
              <style jsx>{`
                .map-container {
                  width: 100%;
                  height: 650px;
                  max-width: 100%;
                }

                @media (max-width: 1024px) {
                  .map-container {
                    height: 670px;
                  }
                }

                @media (max-width: 640px) {
                  .map-container {
                    height: 400px;
                    padding-bottom: 2.5rem;
                  }
                }

                .map-container svg {
                  width: 100% !important;
                  height: 100% !important;
                }
              `}</style>
              <div className="map-container">
                <ComposableMap
                  projection="geoEqualEarth"
                  projectionConfig={{
                    scale: 190,
                    center: [0, 0],
                  }}
                  width={800}
                  height={650}
                >
                  <defs>
                    <filter
                      id="coastShadow"
                      x="-20%"
                      y="-20%"
                      width="140%"
                      height="140%"
                    >
                      <feGaussianBlur
                        in="SourceAlpha"
                        stdDeviation="4"
                        result="blur"
                      />
                      <feColorMatrix
                        in="blur"
                        type="matrix"
                        values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.35 0"
                        result="shadow"
                      />
                      <feMerge>
                        <feMergeNode in="shadow" />
                      </feMerge>
                    </filter>

                    <filter
                      id="flagShadow"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
                      <feOffset dx="0" dy="2" result="offsetblur" />
                      <feComponentTransfer>
                        <feFuncA type="linear" slope="0.25" />
                      </feComponentTransfer>
                      <feMerge>
                        <feMergeNode />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>

                    {/* Define clip paths for circular flags */}
                    {Object.keys(countryCenters).map((name) => {
                      const safeId = name.replace(/\s+/g, "-");
                      return (
                        <clipPath key={`clip-${safeId}`} id={`flag-clip-${safeId}`}>
                          <circle cx="0" cy="0" r="12" />
                        </clipPath>
                      );
                    })}

                    {/* Define patterns for flag images - properly centered and scaled */}
                    {Object.entries(countryCenters).map(([name, { flag }]) => {
                      const safeId = name.replace(/\s+/g, "-");
                      return (
                        <pattern
                          key={`pattern-${safeId}`}
                          id={`flag-pattern-${safeId}`}
                          x="0"
                          y="0"
                          width="1"
                          height="1"
                          patternUnits="objectBoundingBox"
                          viewBox="0 0 24 24"
                        >
                          <image
                            href={flagImages[name] || flag}
                            x="0"
                            y="0"
                            width="24"
                            height="24"
                            preserveAspectRatio="xMidYMid slice"
                          />
                        </pattern>
                      );
                    })}
                  </defs>

                  <g filter="url(#coastShadow)">
                    <Geographies geography={WORLD_TOPO_JSON}>
                      {({ geographies }) =>
                        geographies.map((geo) => (
                          <Geography
                            key={geo.rsmKey + "-shadow"}
                            geography={geo}
                            style={{
                              default: {},
                            }}
                          />
                        ))
                      }
                    </Geographies>
                  </g>

                  <Geographies geography={WORLD_TOPO_JSON}>
                    {({ geographies }) =>
                      geographies.map((geo) => {
                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            style={{
                              default: {
                                fill: "#c2ddf4",
                                outline: "none",
                              },
                              hover: {
                                fill: "#c2ddf4",
                                outline: "none",
                                cursor: "pointer",
                              },
                              pressed: {
                                fill: "#c2ddf4",
                                outline: "none",
                              },
                            }}
                          />
                        );
                      })
                    }
                  </Geographies>

                  {Object.entries(countryCenters).map(([name, { coords }]) => {
                    const isActive = selectedCountry === name;
                    const safeId = name.replace(/\s+/g, "-");
                    return (
                      <Marker key={name} coordinates={coords}>
                        <g
                          onClick={() => setSelectedCountry(name)}
                          style={{ cursor: "pointer" }}
                        >
                          {/* Outer animated ripple ring */}
                          <circle
                            cx="0"
                            cy="0"
                            fill="none"
                            stroke={
                              isActive
                                ? "rgba(82, 241, 241, 0.7)"
                                : "rgba(45, 121, 168, 0.5)"
                            }
                            strokeWidth="1.5"
                          >
                            <animate
                              attributeName="r"
                              values={
                                isActive ? "17.5;22;17.5" : "17.5;20;17.5"
                              }
                              dur="3s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values={isActive ? "0.6;0.15;0.6" : "0.4;0.1;0.4"}
                              dur="3s"
                              repeatCount="indefinite"
                            />
                          </circle>

                          {/* White border circle with shadow */}
                          <circle
                            cx="0"
                            cy="0"
                            r="14"
                            fill="white"
                            filter="url(#flagShadow)"
                          />

                          {/* Flag circle - centered with proper pattern fill */}
                          <g transform="translate(0, 0)">
                            <circle
                              cx="0"
                              cy="0"
                              r="12"
                              fill={`url(#flag-pattern-${safeId})`}
                              clipPath={`url(#flag-clip-${safeId})`}
                            />
                          </g>

                          {/* Active state glow effect */}
                          {isActive && (
                            <circle
                              cx="0"
                              cy="0"
                              r="14"
                              fill="none"
                              stroke="rgba(82, 241, 241, 0.6)"
                              strokeWidth="2"
                            >
                              <animate
                                attributeName="stroke"
                                values="rgba(82, 241, 241, 0.6);rgba(82, 241, 241, 0.9);rgba(82, 241, 241, 0.6)"
                                dur="2.5s"
                                repeatCount="indefinite"
                              />
                            </circle>
                          )}

                          {/* Invisible interaction circle */}
                          <circle
                            cx="0"
                            cy="0"
                            r="16"
                            fill="transparent"
                            style={{ cursor: "pointer" }}
                          >
                            <animate
                              attributeName="r"
                              values="16;17;16"
                              dur="0.3s"
                              begin="mouseover"
                              fill="freeze"
                            />
                            <animate
                              attributeName="r"
                              values="17;16"
                              dur="0.3s"
                              begin="mouseout"
                              fill="freeze"
                            />
                          </circle>
                        </g>
                      </Marker>
                    );
                  })}
                </ComposableMap>
              </div>
            </div>

            <p className="absolute w-full flex flex-col text-center text-[12px] text-white bottom-0 desktop:right-5 mb-5 mobileToDesk:px-5">
              <span className="inline-block">
                *The boundaries and names shown and the designations used
              </span>
              <span>
                on this map do not imply official endorsement or acceptance by
                the United Nations.
              </span>
            </p>
          </div>
          <motion.div
            className="max-w-[450px] tab:max-w-[350px] tab:min-w-[350px] overflow-hidden bg-white flex flex-col h-full rounded-lg shadow-md hover:shadow-2xl transition-all duration-300"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{
              duration: 0,
              border: {
                duration: 1.8,
                ease: "easeInOut",
              },
            }}
          >
            <div className="flex justify-center items-center h-[250px] tab:h-[250px] hover:scale-105 transition-all duration-700 overflow-hidden">
              <Image
                src={
                  selectedCountry.toLowerCase() === "india"
                    ? `/static/images/india-states.webp`
                    : selectedCountry.toLowerCase() === "ghana"
                      ? `/static/images/ghana-states.webp`
                      : selectedCountry.toLowerCase() === "kenya"
                        ? `/static/images/kenya-states.webp`
                        : `/static/images/asean.png`
                }
                alt="country states map"
                className="h-full w-full object-cover"
                width={500}
                height={250}
                priority
              />
            </div>

            <div className="country-slider">
              <div className="px-5 mb-[20px] mt-6">
                <p className="text-[22px] font-poppins text-center mb-3 font-semibold leading-normal text-transparent bg-clip-text bg-[linear-gradient(to_top,#8EBCE5,#11375C)] text-clip mobileMax:text-xmedium">
                  {selectedCountry}
                </p>
                <ul className="list-disc text-small font-medium mb-5 mobileMax:text-xsmall text-[#003350] ml-[20px]">
                  {selectedCountry === "India" && (
                    <>
                      <li>
                        India has established itself as a global leader across
                        all pillars of the energy transition.
                      </li>
                      <li>
                        Since 2000, the country has provided access to
                        electricity to over 700 million people
                      </li>
                      <li>
                        With the G20 Presidency in 2023, India demonstrated its
                        leadership
                      </li>
                    </>
                  )}
                  {selectedCountry === "Kenya" && (
                    <>
                      <li>
                        Kenya is accelerating its energy transition with a strong focus on renewable energy and energy efficiency.
                      </li>
                      <li>
                        The country aims to achieve 100% clean energy access and sustainable development goals.
                      </li>
                      <li>
                        Empowering sustainable growth through targeted energy efficiency programs across key sectors.
                      </li>
                    </>
                  )}
                  {selectedCountry === "Ghana" && (
                    <>
                      <li>
                        Ghana is committed to enhancing energy efficiency across power, industrial, and residential sectors.
                      </li>
                      <li>
                        Driving climate action and sustainable development through clean energy initiatives.
                      </li>
                    </>
                  )}
                  {selectedCountry === "Southeast Asia" && (
                    <>
                      <li>
                        Southeast Asia is rapidly transforming its energy landscape to meet growing demand sustainably.
                      </li>
                      <li>
                        Regional collaboration across ASEAN member states to accelerate energy efficiency.
                      </li>
                      <li>
                        Fostering green investments and climate resilience across key economic sectors.
                      </li>
                    </>
                  )}
                </ul>
                <Link
                  href={getCountryLink(selectedCountry)}
                  target="_blank"
                  className={`px-[23px] py-[12px] flex justify-center max-w-[70%] min-w-[60%] font-poppins bg-[linear-gradient(to_right,#316FA8,#003350)] text-white text-small font-semibold rounded-lg mx-auto text-center mobileMax:max-w-full ${
                    !isLinkAvailable(selectedCountry)
                      ? "pointer-events-none opacity-50 cursor-not-allowed"
                      : "hover:!underline !no-underline"
                  }`}
                  onClick={(e) =>
                    !isLinkAvailable(selectedCountry) && e.preventDefault()
                  }
                >
                  {isLinkAvailable(selectedCountry)
                    ? "Learn More"
                    : "Coming Soon..."}
                  <MdChevronRight className="text-[25px] ml-0.5" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

